import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Gift, Inbox, Loader2, MinusCircle, PlusCircle,
  HandCoins, RefreshCw, Scale, Search, Wallet, X,
} from 'lucide-react';
import client from '../lib/api';
import DateRangeFilter from './components/DateRangeFilter';
import { AdminError } from './components/PageState';
import { EMPTY_DATE_RANGE, dateRangeParams } from './lib/dateRange';
import { useAdminPermissions } from './lib/permissions';
import { formatDateTime, formatMoney, formatNumber } from './lib/formatters';
import { transactionActor, visibleTransactionNote } from './lib/transactions';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import './finance.css';

// Yalnızca varsayılan: sayfa boyutunu yönetici seçer (bkz. lib/pagination).
const PAGE_SIZE = 50;

/**
 * Tür segmenti. Anahtarlar **sunucuyla ortaktır**
 * (`admin_router.MANUAL_KINDS`); ayrışırsa uç 400 döner ve yönetici
 * yazdığı süzgecin neden uygulanmadığını anlayamaz.
 */
export const manualKinds = [
  { id: 'all', label: 'Tümü' },
  { id: 'credit', label: 'Eklemeler' },
  { id: 'debit', label: 'Düşümler' },
  { id: 'bonus', label: 'Bonus' },
];

const KIND_IDS = manualKinds.map((item) => item.id);

/**
 * Listenin istek parametreleri.
 *
 * `kind` yalnızca gerçek bir segment seçiliyken gönderilir: "Tümü" için
 * `all` yollamak sunucudaki beyaz listeye takılırdı.
 */
export const buildManualParams = (
  { skip, kind, query, dateRange, limit = PAGE_SIZE },
) => {
  const params = { limit, skip, scope: 'manual' };
  if (kind !== 'all') params.kind = kind;
  if (query.trim()) params.q = query.trim();
  return { ...params, ...dateRangeParams(dateRange) };
};

/**
 * Özet kartların okunuşu.
 *
 * Uç `summary` alanını yalnızca manuel kapsamda döndürür; beklenmedik bir
 * gövdede kartlar **çizilmez** — uydurma sıfır yazmak yöneticiye "bu ay hiç
 * elle işlem yapılmamış" dedirtirdi (finans özetindeki kuralın aynısı).
 */
export const manualSummaryView = (summary) => {
  if (!summary || typeof summary !== 'object') return null;
  const value = (key) => Number(summary[key] || 0);
  return {
    credited: value('credited_amount'),
    creditedCount: value('credited_count'),
    debited: value('debited_amount'),
    debitedCount: value('debited_count'),
    net: value('net'),
    totalCount: value('total_count'),
    playerCount: value('player_count'),
  };
};

/**
 * Hareketin hangi kutuya düştüğü. Yöntem adı sunucudaki kümeyle aynıdır;
 * kampanya bonusunun yöntemi kampanyanın adını taşıdığı için önektir.
 */
export const manualRowKind = (method = '') => {
  if (method === 'Admin Ekleme') return 'credit';
  if (method === 'Admin Düşme') return 'debit';
  return 'bonus';
};

const kindIconMap = {
  credit: PlusCircle,
  debit: MinusCircle,
  bonus: Gift,
};

const ManualTransactions = () => {
  const { can } = useAdminPermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(() => readPageSize('manual-tx', PAGE_SIZE));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [error, setError] = useState('');
  // İskelet yalnızca ilk yüklemede çizilir; süzgeç değişiminde listeyi
  // iskeletle değiştirmek ekranı okunmaz yapıyordu.
  const firstLoadRef = useRef(true);

  const canSeeUsers = can('users.read');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    if (!silent) setError('');
    try {
      const params = buildManualParams({
        skip, kind, query, dateRange, limit: pageSize });
      const { data } = await client.get('/admin/transactions', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
      setLoadedAt(new Date());
      setError('');
    } catch (requestError) {
      const message = requestError.response?.data?.detail
        || 'Manuel işlemler şu anda alınamıyor.';
      if (!silent) setError(message);
    } finally {
      firstLoadRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, kind, pageSize, query, skip]);

  useEffect(() => {
    // Arama yazılırken her tuşta istek atmamak için kısa bir bekleme; ilk
    // yükleme beklemez.
    const timer = setTimeout(
      () => load({ silent: !firstLoadRef.current }),
      query ? 260 : 0,
    );
    return () => clearTimeout(timer);
  }, [load, query]);

  const view = useMemo(() => manualSummaryView(summary), [summary]);

  const chooseKind = (next) => {
    setKind(KIND_IDS.includes(next) ? next : 'all');
    setSkip(0);
  };
  const chooseQuery = (next) => {
    setQuery(next);
    setSkip(0);
  };
  const chooseDateRange = (next) => {
    setDateRange(next);
    setSkip(0);
  };
  const clearFilters = () => {
    setKind('all');
    setQuery('');
    setDateRange(EMPTY_DATE_RANGE);
    setSkip(0);
  };

  const filtersActive = kind !== 'all' || Boolean(query.trim())
    || dateRange.range !== EMPTY_DATE_RANGE.range;
  const rangeEnd = Math.min(skip + pageSize, total);

  return (
    <div className="fin-page">
      <header className="fin-head">
        <span className="fin-head-mark" aria-hidden="true">
          <HandCoins size={23} />
        </span>
        <div className="fin-head-copy">
          <h1>Manuel İşlemler</h1>
          <p>
            Yönetici eliyle açılan bakiye hareketleri: elle ekleme ve düşme,
            panelden verilen bonus ve bonus iptali. Bir ödeme yöntemiyle
            yapılan yatırım ve çekimler burada değil{' '}
            <Link to="/admin/finance">Finans Geçmişi</Link> sayfasındadır.
          </p>
        </div>
        <div className="fin-head-actions">
          <span className="fin-stamp" title="Listenin son okunma saati">
            <span className="fin-stamp-dot" aria-hidden="true" />
            {loadedAt ? `Son okuma ${formatDateTime(loadedAt)}` : 'Okunuyor…'}
          </span>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => load()}
            disabled={loading || refreshing}
          >
            {refreshing || loading ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw size={14} aria-hidden="true" />
            )}
            Yenile
          </button>
        </div>
      </header>

      {view && (
        <section className="fin-kpis" aria-label="Manuel işlem özeti">
          <button
            type="button"
            className={`fin-kpi ${kind === 'credit' ? 'is-active' : ''}`}
            style={{ '--fin-kpi-tone': 'var(--fin-in)' }}
            onClick={() => chooseKind(kind === 'credit' ? 'all' : 'credit')}
            aria-pressed={kind === 'credit'}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <PlusCircle size={17} />
              </span>
              <span className="fin-kpi-name">Elle eklenen</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.creditedCount)} işlem
              </span>
            </div>
            <div>
              <div className="fin-kpi-value is-in">{formatMoney(view.credited)}</div>
              <div className="fin-kpi-sub">Bakiyeye elle yazılan para</div>
            </div>
          </button>

          <button
            type="button"
            className={`fin-kpi ${kind === 'debit' ? 'is-active' : ''}`}
            style={{ '--fin-kpi-tone': 'var(--fin-out)' }}
            onClick={() => chooseKind(kind === 'debit' ? 'all' : 'debit')}
            aria-pressed={kind === 'debit'}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <MinusCircle size={17} />
              </span>
              <span className="fin-kpi-name">Elle düşülen</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.debitedCount)} işlem
              </span>
            </div>
            <div>
              <div className="fin-kpi-value is-out">{formatMoney(view.debited)}</div>
              <div className="fin-kpi-sub">Bakiyeden elle silinen para</div>
            </div>
          </button>

          <div
            className="fin-kpi is-static"
            style={{ '--fin-kpi-tone': view.net >= 0 ? 'var(--fin-out)' : 'var(--fin-in)' }}
          >
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <Scale size={17} />
              </span>
              <span className="fin-kpi-name">Net etki</span>
              <span className={`fin-kpi-chip ${view.net >= 0 ? '' : 'is-out'}`}>
                {view.net >= 0 ? 'Kasadan çıkan' : 'Kasaya kalan'}
              </span>
            </div>
            <div>
              <div className={`fin-kpi-value ${view.net >= 0 ? 'is-in' : 'is-out'}`}>
                {formatMoney(view.net)}
              </div>
              <div className="fin-kpi-sub">
                Eklenen eksi düşülen; oyun ve ödeme hareketleri bu rakamda yoktur.
              </div>
            </div>
          </div>

          <div className="fin-kpi is-static" style={{ '--fin-kpi-tone': 'var(--fin-wait)' }}>
            <div className="fin-kpi-top">
              <span className="fin-kpi-icon" aria-hidden="true">
                <Wallet size={17} />
              </span>
              <span className="fin-kpi-name">Kayıt</span>
              <span className="fin-kpi-chip">
                {formatNumber(view.playerCount)} üye
              </span>
            </div>
            <div>
              <div className="fin-kpi-value">{formatNumber(view.totalCount)}</div>
              <div className="fin-kpi-sub">Seçili tarih aralığındaki manuel hareket</div>
            </div>
          </div>
        </section>
      )}

      <section className="fin-filters" aria-label="Süzgeçler">
        <div className="fin-filter-row">
          <span className="fin-filter-label">Tür</span>
          <div className="fin-seg" role="group" aria-label="Manuel işlem türü">
            {manualKinds.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => chooseKind(item.id)}
                className={`fin-seg-btn ${kind === item.id ? 'is-active' : ''}`}
                aria-pressed={kind === item.id}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="fin-search">
            <label htmlFor="manual-search" className="sr-only">
              Manuel işlemlerde ara
            </label>
            <Search size={15} aria-hidden="true" />
            <input
              id="manual-search"
              value={query}
              onChange={(event) => chooseQuery(event.target.value)}
              className="admin-input"
              placeholder="Kullanıcı, yöntem veya işlemi yapan yetkili"
            />
            {query && (
              <button
                type="button"
                className="fin-search-clear"
                onClick={() => chooseQuery('')}
                aria-label="Aramayı temizle"
                title="Aramayı temizle"
              >
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="fin-filter-row">
          <span className="fin-filter-label">Tarih</span>
          <DateRangeFilter value={dateRange} onChange={chooseDateRange} />
        </div>

        {filtersActive && (
          <div className="fin-filter-row">
            <span className="fin-filter-label">Etkin</span>
            <div className="fin-chips">
              {kind !== 'all' && (
                <span className="fin-chip">
                  {manualKinds.find((item) => item.id === kind)?.label}
                  <button
                    type="button"
                    onClick={() => chooseKind('all')}
                    aria-label="Tür süzgecini kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              {query.trim() && (
                <span className="fin-chip">
                  {`Arama: ${query.trim()}`}
                  <button
                    type="button"
                    onClick={() => chooseQuery('')}
                    aria-label="Aramayı kaldır"
                  >
                    <X size={10} aria-hidden="true" />
                  </button>
                </span>
              )}
              <button
                type="button"
                className="fin-chips-clear"
                onClick={clearFilters}
              >
                Süzgeçleri temizle
              </button>
            </div>
          </div>
        )}
      </section>

      {error ? (
        <AdminError message={error} onRetry={() => load()} />
      ) : (
        <section className="fin-list" aria-label="Manuel hareketler">
          <div className="fin-list-head">
            <span className="fin-list-title">
              <HandCoins size={15} aria-hidden="true" />
              Manuel hareketler
            </span>
            <span className="fin-list-count">
              {loading ? 'Okunuyor…' : `${formatNumber(total)} kayıt`}
            </span>
            {view && (
              <span className="fin-list-count fin-pager-spacer">
                Özet kartlar seçili tarih ve aramayı gösterir; tür yalnızca
                listeyi süzer.
              </span>
            )}
          </div>

          {loading ? (
            <div
              className="fin-skeleton"
              role="status"
              aria-label="Manuel işlemler yükleniyor"
            >
              <span className="sr-only">Manuel işlemler yükleniyor</span>
              {Array.from({ length: 8 }, (_, index) => (
                <div className="fin-skeleton-row" key={index}>
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="fin-state-box">
              <Inbox size={28} aria-hidden="true" />
              <h2>Manuel işlem bulunamadı</h2>
              <p>
                {filtersActive
                  ? 'Seçili süzgeçlere uyan kayıt yok. Süzgeçleri temizleyip '
                    + 'tüm manuel hareketlere bakabilirsiniz.'
                  : 'Bu kurulumda henüz elle bakiye eklenmemiş ya da '
                    + 'düşülmemiş.'}
              </p>
              {filtersActive && (
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={clearFilters}
                >
                  Süzgeçleri temizle
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>İşlem</th>
                      <th className="is-right">Tutar</th>
                      <th>Açıklama</th>
                      <th>İşlemi yapan</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const rowKind = manualRowKind(item.method);
                      const Icon = kindIconMap[rowKind] || Gift;
                      const incoming = Number(item.amount) >= 0;
                      const note = visibleTransactionNote(item);
                      const actor = transactionActor(item);
                      return (
                        <tr key={item.id}>
                          <td className="fin-cell-user" data-label="Kullanıcı">
                            <div className="fin-user">
                              <span className="fin-avatar" aria-hidden="true">
                                {(item.username || '?').slice(0, 1).toLocaleUpperCase('tr-TR')}
                              </span>
                              <span className="min-w-0">
                                {canSeeUsers && item.user_id ? (
                                  <Link
                                    to={`/admin/users/${item.user_id}`}
                                    className="fin-user-name"
                                    title={`${item.username} üyesinin profilini aç`}
                                  >
                                    {item.username}
                                  </Link>
                                ) : (
                                  <span className="fin-user-name">{item.username}</span>
                                )}
                                {item.balance_after === null
                                  || item.balance_after === undefined ? null : (
                                    <span className="fin-user-meta">
                                      {`İşlem sonrası ${formatMoney(item.balance_after)}`}
                                    </span>
                                  )}
                              </span>
                            </div>
                          </td>
                          <td className="fin-cell-method" data-label="İşlem">
                            <div className="fin-method">
                              <span className={`fin-type ${incoming ? 'deposit' : 'withdraw'}`}>
                                <Icon size={13} aria-hidden="true" />
                                {item.method || 'Manuel işlem'}
                              </span>
                            </div>
                          </td>
                          <td
                            className={`fin-cell-amount is-right ${incoming ? 'is-in' : 'is-out'}`}
                            data-label="Tutar"
                          >
                            {`${incoming ? '+' : '−'}${formatMoney(Math.abs(Number(item.amount) || 0))}`}
                          </td>
                          <td className="fin-cell-method" data-label="Açıklama">
                            {note ? (
                              <span className="fin-note" title={note}>{note}</span>
                            ) : (
                              <span className="fin-blank">—</span>
                            )}
                          </td>
                          <td className="fin-cell-actor" data-label="İşlemi yapan">
                            {actor ? (
                              <span
                                className="fin-actor"
                                title={`İşlemi yapan yetkili: ${actor}`}
                              >
                                {actor}
                              </span>
                            ) : (
                              // Oyuncunun kendi bonus iptalinde yetkili yoktur;
                              // yer tutucu bir ad yazmak yanlış bilgi olurdu.
                              <span className="fin-blank">—</span>
                            )}
                          </td>
                          <td className="fin-cell-date" data-label="Tarih">
                            {formatDateTime(item.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fin-pager">
                <button
                  type="button"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - pageSize))}
                  className="admin-btn-secondary"
                >
                  <ChevronLeft size={14} aria-hidden="true" />
                  Önceki
                </button>
                <span className="fin-pager-info" aria-live="polite">
                  <b>{formatNumber(skip + 1)}</b>
                  {' – '}
                  <b>{formatNumber(rangeEnd)}</b>
                  {` / ${formatNumber(total)} kayıt`}
                </span>
                <PageSizeSelect
                  value={pageSize}
                  disabled={loading}
                  onChange={(size) => {
                    setSkip(0);
                    setPageSize(size);
                    writePageSize('manual-tx', size);
                  }}
                />
                <button
                  type="button"
                  disabled={skip + pageSize >= total}
                  onClick={() => setSkip(skip + pageSize)}
                  className="admin-btn-secondary fin-pager-spacer"
                >
                  Sonraki
                  <ChevronRight size={14} aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ManualTransactions;
